/**
 * cred-broker config wizard — a NAMED credential connector whose config is a single
 * bash SCRIPT the Universal Builder sources before the agent works (see
 * doc/arch/vertical/TOOLS_CONNECTORS.md §8b). Follows the SAME dual-mode contract as
 * {@link ./ToolGenericRestConfigWizard.jsx}:
 *
 *   - ``persistTarget='agent'`` (inline, from the Agent tool picker): name + script →
 *     ``putToolSecret`` under a per-agent SSM path → ``onSave({name,instanceId,
 *     instanceLabel,config,secretRefs})``. The chip then renders as ``name · cred-broker``
 *     with an Edit affordance (parity with code-connector / generic-rest).
 *   - ``persistTarget='registry'`` (from the Connections tab): name + script →
 *     ``createSecretsRegistrySecret`` / ``updateSecretsRegistrySecret`` (reusable row) →
 *     ``onRegistryCreated(saved)``.
 *
 * cred-broker is simpler than code-connector (no transport/provider/live-test): the user
 * just names it and pastes the script — so this is a compact single-step form, not a
 * multi-step stepper. The one secret property is ``BOOTSTRAP_SCRIPT`` (``x-secret``); SSM
 * Standard tier caps a SecureString value at 4 KB, enforced here on save.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, TerminalSquare } from 'lucide-react';
import {
  buildToolSecretPath,
  createSecretsRegistrySecret,
  newToolInstanceId,
  putToolSecret,
  testSecretsRegistrySecret,
  updateSecretsRegistrySecret,
} from '../../services/toolsApi';

// A saved broker is TESTED right away (the server checks the script is stored — an agent's first run proves it):
// the builder only uses a TESTED connection, and before this a broker could never become one.
async function testSaved(row) {
  try {
    const t = await testSecretsRegistrySecret(row.secretId, {});
    return { ...row, testStatus: t?.testStatus || (t?.ok ? 'TESTED_OK' : row.testStatus) };
  } catch {
    return row;
  }
}

const TOOL_NAME = 'cred-broker';
const SECRET_KEY = 'BOOTSTRAP_SCRIPT';
const SSM_MAX_BYTES = 4096;
const SECRET_PLACEHOLDER = '•••••••• (saved — leave blank to keep)';

const SCRIPT_PLACEHOLDER = `# Sourced in the builder shell BEFORE the agent works, AFTER platform creds are stripped.
# Set up whatever credentials/CLIs the build needs. Prefer MINTING short-lived creds
# at runtime over pasting long-lived tokens.
#
# export AWS_ACCESS_KEY_ID=...
# export AWS_SECRET_ACCESS_KEY=...
# az login --service-principal -u "$APP_ID" -p "$SECRET" --tenant "$TENANT"
# gcloud auth activate-service-account --key-file=<(printf '%s' "$GCP_SA_JSON")`;

function _byteLen(s) {
  try { return new TextEncoder().encode(String(s ?? '')).length; }
  catch { return String(s ?? '').length; }
}

function validateLabel(label) {
  const s = String(label || '').trim();
  if (!s) return { ok: false, hint: 'Give this connection a short, memorable name.' };
  if (s.length > 120) return { ok: false, hint: 'Use a shorter name (≤ 120 characters).' };
  return { ok: true };
}

/** Registry row → wizard initial state (for the Connections Edit button). */
export function mapRegistryCredBrokerSecretToInitial(secret) {
  if (!secret || typeof secret !== 'object') return undefined;
  let v = secret.version;
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    const n = v != null ? Number(v) : NaN;
    v = Number.isFinite(n) ? n : undefined;
  }
  return {
    instanceId: 'ti_registry_existing',
    instanceLabel: String(secret.secretName || '').trim(),
    secretId: String(secret.secretId || '').trim(),
    secretName: secret.secretName,
    config: secret.config && typeof secret.config === 'object' ? { ...secret.config } : {},
    secretRefs: secret.secretRefs && typeof secret.secretRefs === 'object' ? { ...secret.secretRefs } : {},
    registryLogicalVersion: typeof v === 'number' ? v : undefined,
    hasSavedScript: Boolean(secret.secretRefs && secret.secretRefs[SECRET_KEY]),
  };
}

/**
 * @param {{
 *   tool?: { name: string, description?: string },
 *   open: boolean,
 *   isNewEntry: boolean,
 *   initialEntry?: { instanceId?: string, instanceLabel?: string, config?: object, secretRefs?: object, secretId?: string, registryLogicalVersion?: number, hasSavedScript?: boolean },
 *   networkId?: string,
 *   agentId?: string,
 *   env?: string,
 *   tenantId: string,
 *   persistTarget?: 'agent' | 'registry',
 *   registryScope?: Record<string, unknown>,
 *   onSave?: (entry: { name: string, instanceId: string, instanceLabel: string, config: object, secretRefs: object }) => void,
 *   onRegistryCreated?: (saved: object) => void,
 *   onCancel: () => void,
 * }} props
 */
export default function ToolCredBrokerConfigWizard({
  tool,
  open,
  isNewEntry,
  initialEntry,
  networkId,
  agentId,
  env,
  tenantId,
  persistTarget = 'agent',
  registryScope,
  onSave,
  onRegistryCreated,
  onCancel,
}) {
  const isRegistry = persistTarget === 'registry';
  const [instanceId] = useState(() => initialEntry?.instanceId || newToolInstanceId());
  const [instanceLabel, setInstanceLabel] = useState(() => String(initialEntry?.instanceLabel || '').trim());
  // A saved script is never echoed back. On edit we start blank + "replacing=false"
  // (keep existing) and only PUT a new value when the user types one.
  const hasSaved = Boolean(
    (initialEntry?.secretRefs && initialEntry.secretRefs[SECRET_KEY]) || initialEntry?.hasSavedScript,
  );
  const [script, setScript] = useState('');
  const [replacing, setReplacing] = useState(() => isNewEntry || !hasSaved);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) { setError(''); return; }
    setInstanceLabel(String(initialEntry?.instanceLabel || '').trim());
  }, [open, initialEntry?.instanceLabel]);

  const scriptBytes = useMemo(() => _byteLen(script), [script]);
  const overSize = scriptBytes > SSM_MAX_BYTES;
  const labelCheck = validateLabel(instanceLabel);
  // Need a script when creating, or when the user chose to Replace and there's no saved one.
  const scriptProvided = Boolean(script.trim());
  const scriptSatisfied = replacing ? scriptProvided : hasSaved;
  const canSave = labelCheck.ok && scriptSatisfied && !overSize && !submitting;

  if (!open) return null;
  const toolName = String(tool?.name || TOOL_NAME);

  const registryEditId = isRegistry ? String(initialEntry?.secretId || '').trim() : '';

  const handleSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    setError('');
    try {
      const label = instanceLabel.trim();

      if (isRegistry) {
        // Reusable registry row — the script is a plaintextSecret the server encrypts.
        const plaintextSecrets = replacing && scriptProvided ? { [SECRET_KEY]: script } : {};
        if (registryEditId) {
          const lv = initialEntry?.registryLogicalVersion;
          const ifMatchOpt =
            typeof lv === 'number' && Number.isFinite(lv) ? { ifMatch: String(Math.trunc(lv)) } : {};
          const data = await updateSecretsRegistrySecret(
            registryEditId,
            { secretName: label, config: {}, ...(Object.keys(plaintextSecrets).length ? { plaintextSecrets } : {}) },
            ifMatchOpt,
          );
          const updated = data?.secret && typeof data.secret === 'object' ? data.secret : null;
          if (!updated) throw new Error('Unexpected response from secrets registry');
          onRegistryCreated?.(await testSaved(updated));
          return;
        }
        const data = await createSecretsRegistrySecret({
          toolName,
          secretName: label,
          config: {},
          plaintextSecrets: { [SECRET_KEY]: script },
          scope: registryScope,
        });
        const created = data?.secret && typeof data.secret === 'object' ? data.secret : null;
        if (!created?.secretId) throw new Error('Unexpected response from secrets registry');
        onRegistryCreated?.(await testSaved(created));
        return;
      }

      // Agent (inline) mode — PUT the script to a per-agent SSM path, ref it in secretRefs.
      const secretRefs = { ...(initialEntry?.secretRefs || {}) };
      if (replacing && scriptProvided) {
        const parameterName = buildToolSecretPath({
          env, tenantId, networkId, agentId, instanceId, configKey: SECRET_KEY,
        });
        await putToolSecret({ parameterName, value: String(script) });
        secretRefs[SECRET_KEY] = parameterName;
      }
      onSave?.({
        name: toolName,
        instanceId,
        instanceLabel: label,
        config: {},
        secretRefs,
      });
    } catch (exc) {
      setError(exc?.message || 'Failed to save cred-broker configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cred-broker-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-start gap-2">
            <TerminalSquare className="mt-0.5 h-5 w-5 text-gray-500" aria-hidden="true" />
            <div>
              <h2 id="cred-broker-title" className="text-base font-semibold text-gray-900">
                {registryEditId || (!isNewEntry && !isRegistry) ? 'Edit' : 'New'} credential broker
              </h2>
              <p className="mt-1 text-xs text-gray-600">
                A bash script the builder sources before the agent works — set up AWS/Azure/GCP/CLI
                credentials for the build. Runs after platform creds are stripped.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="px-2.5 py-1 text-xs font-medium text-gray-500 rounded-md hover:bg-gray-100"
          >
            ×
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label htmlFor="cred-broker-label" className="block text-sm font-medium text-gray-700 mb-1">
              Connection name<span className="text-red-500"> *</span>
            </label>
            <input
              id="cred-broker-label"
              type="text"
              data-testid="cred-broker-label"
              autoComplete="off"
              value={instanceLabel}
              onChange={(e) => setInstanceLabel(e.target.value)}
              placeholder="e.g. Prod AWS — assume DeployRole"
              className={inputClass}
            />
            <p className="mt-1 text-xs text-gray-500">
              Shown as “{instanceLabel.trim() || 'name'} · {toolName}”. Required so multiple brokers stay identifiable.
            </p>
          </div>

          <div>
            <label htmlFor="cred-broker-script" className="block text-sm font-medium text-gray-700 mb-1">
              Bootstrap script (bash){replacing && <span className="text-red-500"> *</span>}
            </label>
            {hasSaved && !replacing ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={SECRET_PLACEHOLDER}
                  readOnly
                  className={`${inputClass} bg-gray-100 text-gray-500`}
                />
                <button
                  type="button"
                  onClick={() => { setReplacing(true); setScript(''); }}
                  className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50 whitespace-nowrap"
                >
                  Replace…
                </button>
              </div>
            ) : (
              <>
                <textarea
                  id="cred-broker-script"
                  data-testid="cred-broker-script"
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  placeholder={SCRIPT_PLACEHOLDER}
                  rows={12}
                  spellCheck={false}
                  className={`${inputClass} font-mono text-xs`}
                />
                <div className="mt-1 flex items-center justify-between">
                  <p className={`text-xs tabular-nums ${overSize ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                    {scriptBytes.toLocaleString()} / {SSM_MAX_BYTES.toLocaleString()} bytes
                    {overSize ? ' — too large; SSM caps a secret at 4 KB. Mint creds at runtime instead of pasting large blobs.' : ''}
                  </p>
                  {hasSaved && replacing && (
                    <button
                      type="button"
                      onClick={() => { setReplacing(false); setScript(''); }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                    >
                      Keep saved
                    </button>
                  )}
                </div>
              </>
            )}
            <p className="mt-1 text-xs text-gray-500">
              The script is encrypted (platform CMK) and never echoed back. It may install CLIs and mint
              temporary tokens at runtime.
            </p>
          </div>

          {!labelCheck.ok && instanceLabel.length > 0 && (
            <p className="text-xs text-red-600">{labelCheck.hint}</p>
          )}
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="cred-broker-save"
            onClick={handleSave}
            disabled={!canSave}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
